import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import Groups from "@/pages/Groups";
import Agents from "@/pages/Agents";
import AuditLog from "@/pages/AuditLog";
import Profile from "@/pages/Profile";
import AuthCallback from "@/pages/AuthCallback";
import TicketsList from "@/pages/TicketsList";
import TicketNew from "@/pages/TicketNew";
import TicketDetail from "@/pages/TicketDetail";
import CannedResponses from "@/pages/CannedResponses";
import TicketTemplates from "@/pages/TicketTemplates";
import AdminCenter from "@/pages/AdminCenter";
import Reports from "@/pages/Reports";
import EmailLogs from "@/pages/EmailLogs";
import ErrorLogs from "@/pages/ErrorLogs";
import InternalDashboard from "@/pages/InternalDashboard";
import Unsubscribe from "@/pages/Unsubscribe";
import { Toaster } from "@/components/ui/sonner";

// Handle session_id in URL fragment (from Google Auth) synchronously during render
function Router() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />

            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/tickets" element={<TicketsList />} />
                <Route path="/tickets/new" element={<TicketNew />} />
                <Route path="/tickets/:id" element={<TicketDetail />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/internal/dashboard" element={<InternalDashboard />} />
                <Route
                    path="/admin/canned-responses"
                    element={<ProtectedRoute roles={["agent", "admin", "superadmin"]}><CannedResponses /></ProtectedRoute>}
                />
                <Route
                    path="/admin/ticket-templates"
                    element={<ProtectedRoute roles={["admin", "superadmin"]}><TicketTemplates /></ProtectedRoute>}
                />
                <Route
                    path="/admin/error-logs"
                    element={<ProtectedRoute roles={["admin", "superadmin"]}><ErrorLogs /></ProtectedRoute>}
                />
                <Route
                    path="/admin/center"
                    element={<ProtectedRoute roles={["admin", "superadmin"]}><AdminCenter /></ProtectedRoute>}
                />
                <Route
                    path="/admin/companies"
                    element={<ProtectedRoute roles={["admin", "superadmin"]}><Companies /></ProtectedRoute>}
                />
                <Route
                    path="/admin/groups"
                    element={<ProtectedRoute roles={["admin", "superadmin"]}><Groups /></ProtectedRoute>}
                />
                <Route
                    path="/admin/agents"
                    element={<ProtectedRoute roles={["admin", "superadmin"]}><Agents /></ProtectedRoute>}
                />
                <Route
                    path="/admin/audit"
                    element={<ProtectedRoute roles={["superadmin"]}><AuditLog /></ProtectedRoute>}
                />
                <Route
                    path="/admin/email-logs"
                    element={<ProtectedRoute roles={["admin", "superadmin"]}><EmailLogs /></ProtectedRoute>}
                />
            </Route>

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Router />
                    <Toaster richColors position="top-right" />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
